import { NavLink, useNavigate, useLocation } from "react-router-dom";
import {
  LayoutDashboard, Building2, GraduationCap, Users, Presentation, BookOpen,
  CalendarDays, ClipboardList, FileText, Receipt, CreditCard, Wallet, Package,
  Boxes, CircleDollarSign, Landmark, Settings, ShieldCheck, LogOut, Menu, X,
  QrCode, FileStack, UtensilsCrossed, NotebookPen, Banknote, School, HelpCircle,
  ChevronDown, ChevronRight, PanelLeftClose, PanelLeft,
} from "lucide-react";
import { useAuth } from "../context/AuthContext";
import { AIChatWidget } from "./AIChatWidget";
import { LanguageSwitcher } from "./LanguageSwitcher";
import { useLang } from "../i18n/LanguageContext";
import api from "../lib/api";
import { useEffect, useState } from "react";

export function usePlatformBrand() {
  const [brand, setBrand] = useState(null);
  useEffect(() => {
    api.get("/platform/settings").then((r) => setBrand(r.data)).catch(() => {});
  }, []);
  return brand;
}

const GROUPS = [
  { key: "plataforma", label: "Plataforma" },
  { key: "academico", label: "Académico" },
  { key: "finanzas", label: "Finanzas" },
  { key: "administracion", label: "Administración" },
];

const MENU = [
  { to: "/colegios", label: "Colegios", icon: Building2, roles: ["superadmin"], group: "plataforma" },
  { to: "/suscripciones", label: "Suscripciones", icon: Package, roles: ["superadmin"], group: "plataforma" },
  { to: "/facturacion", label: "Facturación", icon: Receipt, roles: ["superadmin"], group: "plataforma" },
  { to: "/pagos-plataforma", label: "Pagos", icon: CreditCard, roles: ["superadmin"], group: "plataforma" },
  { to: "/empresa", label: "Mi Empresa", icon: Landmark, roles: ["superadmin"], group: "plataforma" },
  { to: "/dashboard", label: "Dashboard", icon: LayoutDashboard, roles: ["admin", "secretaria", "contabilidad"], group: "academico", school: true },
  { to: "/estudiantes", label: "Estudiantes", icon: GraduationCap, roles: ["admin", "secretaria", "maestro"], group: "academico", school: true },
  { to: "/padres", label: "Padres/Tutores", icon: Users, roles: ["admin", "secretaria"], group: "academico", school: true },
  { to: "/maestros", label: "Maestros", icon: Presentation, roles: ["admin", "secretaria"], group: "academico", school: true },
  { to: "/materias", label: "Materias", icon: BookOpen, roles: ["admin", "secretaria", "maestro"], group: "academico", school: true },
  { to: "/cursos", label: "Cursos", icon: FileStack, roles: ["admin"], group: "academico", school: true },
  { to: "/actividades", label: "Actividades", icon: CalendarDays, roles: ["admin", "secretaria", "maestro"], group: "academico", school: true },
  { to: "/horarios", label: "Horarios", icon: CalendarDays, roles: ["admin", "secretaria", "maestro"], group: "academico", school: true },
  { to: "/menu-comedor", label: "Menú Comedor", icon: UtensilsCrossed, roles: ["admin", "secretaria", "maestro"], group: "academico", school: true },
  { to: "/planificaciones", label: "Planificaciones", icon: NotebookPen, roles: ["admin", "maestro"], group: "academico", school: true },
  { to: "/classroom", label: "Google Classroom", icon: School, roles: ["admin", "maestro"], group: "academico", school: true },
  { to: "/tareas", label: "Tareas", icon: ClipboardList, roles: ["admin", "secretaria", "maestro"], group: "academico", school: true },
  { to: "/calificaciones", label: "Calificaciones", icon: FileText, roles: ["admin", "maestro"], group: "academico", school: true },
  { to: "/conceptos", label: "Conceptos", icon: Receipt, roles: ["admin", "contabilidad"], group: "finanzas", school: true },
  { to: "/descuentos", label: "Descuentos", icon: Wallet, roles: ["admin", "contabilidad"], group: "finanzas", school: true },
  { to: "/combos", label: "Combos", icon: Package, roles: ["admin"], group: "finanzas", school: true },
  { to: "/facturas", label: "Facturas", icon: Receipt, roles: ["admin", "secretaria", "contabilidad"], group: "finanzas", school: true },
  { to: "/pagos", label: "Pagos", icon: CreditCard, roles: ["admin", "secretaria", "contabilidad"], group: "finanzas", school: true },
  { to: "/gastos", label: "Gastos", icon: Wallet, roles: ["admin", "contabilidad"], group: "finanzas", school: true },
  { to: "/productos", label: "Inventario", icon: Boxes, roles: ["admin", "contabilidad"], group: "finanzas", school: true },
  { to: "/cierre-caja", label: "Cierre de Caja", icon: CircleDollarSign, roles: ["admin", "contabilidad"], group: "finanzas", school: true },
  { to: "/bancos", label: "Cuentas Bancarias", icon: Landmark, roles: ["admin"], group: "finanzas", school: true },
  { to: "/nomina", label: "Nómina", icon: Banknote, roles: ["admin", "contabilidad"], group: "finanzas", school: true },
  { to: "/documentos", label: "Documentos", icon: FileStack, roles: ["admin", "secretaria"], group: "administracion", school: true },
  { to: "/usuarios", label: "Usuarios", icon: Users, roles: ["admin"], group: "administracion", school: true },
  { to: "/configuracion", label: "Configuración y QR", icon: QrCode, roles: ["admin"], group: "administracion", school: true },
  { to: "/auditoria", label: "Auditoría", icon: ShieldCheck, roles: ["admin"], group: "administracion", school: true },
  { to: "/permisos", label: "Permisos", icon: ShieldCheck, roles: ["superadmin", "admin"], group: "administracion" },
  { to: "/portal", label: "Mi Portal", icon: Users, roles: ["padre"] },
  { to: "/maestro", label: "Mi Portal", icon: Presentation, roles: ["maestro"] },
  { to: "/manual", label: "Manual de Uso", icon: HelpCircle, roles: ["superadmin", "admin", "secretaria", "contabilidad", "maestro", "padre"] },
];

export default function Layout({ children }) {
  const { user, logout } = useAuth();
  const { t } = useLang();
  const brand = usePlatformBrand();
  const navigate = useNavigate();
  const location = useLocation();
  const [mobileOpen, setMobileOpen] = useState(false);
  const [collapsed, setCollapsed] = useState(localStorage.getItem("sidebar-collapsed") === "1");
  const [closedGroups, setClosedGroups] = useState(() => {
    try { return JSON.parse(localStorage.getItem("menu-closed") || "{}"); } catch { return {}; }
  });
  const schoolName = localStorage.getItem("activeSchoolName") || user?.school?.name || "";

  const items = MENU.filter((m) => m.roles.includes(user?.role));
  const i18nOn = user?.role === "superadmin" || !!user?.school?.i18n_enabled;

  useEffect(() => {
    const activeGroup = GROUPS.find((g) => items.some((m) => m.group === g.key && location.pathname.startsWith(m.to)));
    if (activeGroup && closedGroups[activeGroup.key]) {
      const next = { ...closedGroups, [activeGroup.key]: false };
      setClosedGroups(next);
      localStorage.setItem("menu-closed", JSON.stringify(next));
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [location.pathname]);

  const doLogout = () => {
    logout();
    navigate("/login");
  };

  const toggleGroup = (key) => {
    const next = { ...closedGroups, [key]: !closedGroups[key] };
    setClosedGroups(next);
    localStorage.setItem("menu-closed", JSON.stringify(next));
  };

  const toggleCollapsed = () => {
    const next = !collapsed;
    setCollapsed(next);
    localStorage.setItem("sidebar-collapsed", next ? "1" : "0");
  };

  const renderItem = (m) => (
    <NavLink
      key={m.to}
      to={m.to}
      title={m.label}
      onClick={() => setMobileOpen(false)}
      data-testid={`nav-${m.to.slice(1)}`}
      className={({ isActive }) =>
        `flex items-center gap-3 px-3 py-2 rounded-lg text-sm transition-colors ${collapsed ? "justify-center px-2" : ""} ${
          isActive ? "bg-sky-500/15 text-sky-300 font-medium" : "hover:bg-slate-800 hover:text-white"
        }`
      }
    >
      <m.icon className="w-4 h-4 shrink-0" />
      {!collapsed && t(`m.${m.to.slice(1)}`)}
    </NavLink>
  );

  const sidebar = (
    <div className={`flex flex-col h-full bg-slate-900 text-slate-300 transition-[width] duration-200 ${collapsed ? "w-16" : "w-64"}`}>
      <div className={`py-5 border-b border-slate-800 flex items-center gap-3 ${collapsed ? "justify-center px-2" : "px-5"}`}>
        {(brand?.icon_url || brand?.logo_url) && (
          <img src={brand.icon_url || brand.logo_url} alt="icono" className="h-8 w-8 rounded-lg object-contain bg-white/90 shrink-0" data-testid="sidebar-icon" />
        )}
        {!collapsed && (
          <div className="min-w-0 flex-1">
            <p className="text-white font-bold text-lg tracking-tight leading-tight truncate" style={{ fontFamily: "Outfit, sans-serif" }}>
              {brand?.company_name || "EduGestión RD"}
            </p>
            <p className="text-xs text-slate-500 mt-0.5">SaaS Multi-Colegio</p>
          </div>
        )}
        <button
          data-testid="sidebar-collapse-toggle"
          title={collapsed ? "Expandir menú" : "Plegar menú"}
          onClick={toggleCollapsed}
          className="hidden md:flex p-1.5 rounded-lg text-slate-400 hover:text-white hover:bg-slate-800 transition-colors shrink-0"
        >
          {collapsed ? <PanelLeft className="w-4 h-4" /> : <PanelLeftClose className="w-4 h-4" />}
        </button>
      </div>
      <nav className="flex-1 overflow-y-auto py-2 px-2" data-testid="sidebar-nav">
        {GROUPS.map((g) => {
          const groupItems = items.filter((m) => m.group === g.key);
          if (!groupItems.length) return null;
          const closed = !!closedGroups[g.key];
          return (
            <div key={g.key} className="pt-1.5" data-testid={`menu-group-${g.key}`}>
              {!collapsed && (
                <button
                  data-testid={`menu-group-toggle-${g.key}`}
                  onClick={() => toggleGroup(g.key)}
                  className="w-full flex items-center justify-between px-3 py-1.5 text-[11px] font-semibold uppercase tracking-wider text-slate-500 hover:text-slate-300 transition-colors"
                >
                  {t(`grp.${g.key}`)}
                  {closed ? <ChevronRight className="w-3.5 h-3.5" /> : <ChevronDown className="w-3.5 h-3.5" />}
                </button>
              )}
              {(!closed || collapsed) && <div className="space-y-0.5">{groupItems.map(renderItem)}</div>}
            </div>
          );
        })}
        {items.filter((m) => !m.group).map(renderItem)}
      </nav>
      <div className="p-2 border-t border-slate-800">
        <button
          data-testid="logout-button"
          title="Cerrar sesión"
          onClick={doLogout}
          className={`flex items-center gap-3 px-3 py-2 rounded-lg text-sm w-full hover:bg-slate-800 hover:text-white transition-colors ${collapsed ? "justify-center px-2" : ""}`}
        >
          <LogOut className="w-4 h-4 shrink-0" /> {!collapsed && t("common.logout")}
        </button>
      </div>
    </div>
  );

  return (
    <div className="min-h-screen bg-slate-50 flex">
      <aside className="hidden md:block fixed inset-y-0 left-0 z-30">{sidebar}</aside>
      {mobileOpen && (
        <div className="fixed inset-0 z-40 md:hidden">
          <div className="absolute inset-0 bg-black/50" onClick={() => setMobileOpen(false)} />
          <aside className="absolute inset-y-0 left-0 z-50">{sidebar}</aside>
        </div>
      )}
      <div className={`flex-1 ${collapsed ? "md:ml-16" : "md:ml-64"} flex flex-col min-h-screen min-w-0 transition-[margin] duration-200`}>
        <header className="sticky top-0 z-20 bg-white/85 backdrop-blur-md border-b border-slate-200 px-4 sm:px-6 py-3 flex items-center gap-3 min-w-0">
          <button className="md:hidden text-slate-600" data-testid="mobile-menu-button" onClick={() => setMobileOpen(!mobileOpen)}>
            {mobileOpen ? <X className="w-5 h-5" /> : <Menu className="w-5 h-5" />}
          </button>
          {schoolName && (
            <span className="text-sm font-semibold text-slate-800 truncate" data-testid="active-school-name">
              {schoolName}
            </span>
          )}
          <span className="hidden sm:inline-flex text-xs font-medium px-2.5 py-0.5 rounded-full bg-amber-50 text-amber-700 border border-amber-200" data-testid="demo-badge">
            {t("common.demo")}
          </span>
          <div className="ml-auto flex items-center gap-3">
            {i18nOn && <LanguageSwitcher testid="language-switcher-header" />}
            <div className="text-right">
              <p className="text-sm font-medium text-slate-800 leading-tight" data-testid="current-user-name">{user?.name}</p>
              <p className="text-xs text-slate-500 capitalize" data-testid="current-user-role">{user?.role}</p>
            </div>
            <div className="w-9 h-9 rounded-full bg-blue-800 text-white flex items-center justify-center text-sm font-semibold">
              {user?.name?.[0] || "U"}
            </div>
          </div>
        </header>
        <main className="flex-1 p-4 sm:p-6 lg:p-8 max-w-7xl w-full min-w-0">{children}</main>
      </div>
      <AIChatWidget />
    </div>
  );
}
